import React, { useRef, useState, useEffect, useCallback } from 'react'
import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Tổng hợp & phân tích lực (Lớp 10 — Động lực học).
//  • Tổng hợp: kéo 2 vec-tơ lực F₁, F₂ chung gốc → hợp lực F theo quy tắc hình bình hành.
//    |F| = √(F₁²+F₂²+2F₁F₂cosα).
//  • Phân tích: kéo 1 lực F, xoay trục → 2 thành phần Fₓ (//trục) và F_y (⊥trục); F·cos / F·sin.
const W = 620, H = 380
const SCALE = 16          // px / N
const MAXF = 10           // N

type Vec = { x: number; y: number }   // toạ độ vật lí: x phải, y LÊN
const mag = (v: Vec) => Math.hypot(v.x, v.y)
const clampMag = (v: Vec, mx: number): Vec => { const m = mag(v); return m > mx ? { x: v.x / m * mx, y: v.y / m * mx } : v }

const ForceVectorSimulation: React.FC<Props> = ({
  title = 'Tổng hợp & phân tích lực',
  description = 'Kéo các vec-tơ lực để thay đổi độ lớn và hướng. Tổng hợp: hợp lực theo quy tắc hình bình hành. Phân tích: tách 1 lực thành 2 thành phần theo trục (xoay được).',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const [mode, setMode] = useState<'compose' | 'decompose'>('compose')
  const [f1, setF1] = useState<Vec>({ x: 6, y: 0 })
  const [f2, setF2] = useState<Vec>({ x: 0, y: 5 })
  const [fA, setFA] = useState<Vec>({ x: 7, y: 4 })   // lực để phân tích
  const [axisDeg, setAxisDeg] = useState(0)            // góc trục Ox' so với phương ngang
  const dragRef = useRef<null | 'f1' | 'f2' | 'fA'>(null)
  const firstRef = useRef(false)
  const fire = () => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } }

  const O = { x: 200, y: H / 2 }   // gốc chung (lệch trái để chừa chỗ bảng số)

  const sx = (v: Vec) => O.x + v.x * SCALE
  const sy = (v: Vec) => O.y - v.y * SCALE

  // base = chữ chính (F); sub = chỉ số dưới (1/2/x/y). Vẽ: F nghiêng + chỉ số nhỏ + MŨI TÊN VEC-TƠ trên chữ F.
  const drawArrow = (ctx: CanvasRenderingContext2D, v: Vec, color: string, base: string, sub = '', lw = 3.5) => {
    if (mag(v) < 0.05) return
    const ex = sx(v), ey = sy(v)
    const ux = (ex - O.x), uy = (ey - O.y); const L = Math.hypot(ux, uy); const nx = ux / L, ny = uy / L
    ctx.strokeStyle = color; ctx.fillStyle = color; ctx.lineWidth = lw
    ctx.beginPath(); ctx.moveTo(O.x, O.y); ctx.lineTo(ex, ey); ctx.stroke()
    const ah = 11
    ctx.beginPath(); ctx.moveTo(ex, ey)
    ctx.lineTo(ex - ah * nx - ah * 0.55 * ny, ey - ah * ny + ah * 0.55 * nx)
    ctx.lineTo(ex - ah * nx + ah * 0.55 * ny, ey - ah * ny - ah * 0.55 * nx)
    ctx.closePath(); ctx.fill()
    // nhãn: F (nghiêng) + chỉ số dưới + dấu vec-tơ trên chữ F
    const lx = ex + nx * 11 - 6, ly = ey + ny * 11 + 6
    ctx.fillStyle = color; ctx.font = 'bold italic 16px sans-serif'
    ctx.fillText(base, lx, ly)
    const wB = ctx.measureText(base).width
    if (sub) { ctx.font = 'bold italic 11px sans-serif'; ctx.fillText(sub, lx + wB + 1, ly + 4) }
    const ovy = ly - 15
    ctx.strokeStyle = color; ctx.lineWidth = 1.5
    ctx.beginPath(); ctx.moveTo(lx - 1, ovy); ctx.lineTo(lx + wB + 2, ovy); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(lx + wB + 2, ovy); ctx.lineTo(lx + wB - 2, ovy - 3); ctx.lineTo(lx + wB - 2, ovy + 3); ctx.closePath(); ctx.fill()
    // núm kéo
    ctx.beginPath(); ctx.arc(ex, ey, 5, 0, Math.PI * 2); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = 2; ctx.strokeStyle = color; ctx.stroke()
  }

  const draw = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    ctx.clearRect(0, 0, W, H); ctx.fillStyle = '#f8fafc'; ctx.fillRect(0, 0, W, H)

    // Lưới + trục qua O
    ctx.strokeStyle = '#eef2f7'; ctx.lineWidth = 1
    for (let gx = O.x % 40; gx < W; gx += 40) { ctx.beginPath(); ctx.moveTo(gx, 0); ctx.lineTo(gx, H); ctx.stroke() }
    for (let gy = O.y % 40; gy < H; gy += 40) { ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(W, gy); ctx.stroke() }
    ctx.strokeStyle = '#cbd5e1'; ctx.lineWidth = 1.5
    ctx.beginPath(); ctx.moveTo(0, O.y); ctx.lineTo(W, O.y); ctx.moveTo(O.x, 0); ctx.lineTo(O.x, H); ctx.stroke()
    ctx.fillStyle = '#0f172a'; ctx.beginPath(); ctx.arc(O.x, O.y, 4, 0, Math.PI * 2); ctx.fill()

    if (mode === 'compose') {
      const F: Vec = { x: f1.x + f2.x, y: f1.y + f2.y }
      // hình bình hành (nét đứt)
      ctx.strokeStyle = '#94a3b8'; ctx.lineWidth = 1.5; ctx.setLineDash([5, 4])
      ctx.beginPath(); ctx.moveTo(sx(f1), sy(f1)); ctx.lineTo(sx(F), sy(F)); ctx.lineTo(sx(f2), sy(f2)); ctx.stroke(); ctx.setLineDash([])
      drawArrow(ctx, F, '#7c3aed', 'F', '', 5)   // hợp lực
      drawArrow(ctx, f1, '#2563eb', 'F', '1')
      drawArrow(ctx, f2, '#0891b2', 'F', '2')
      // số liệu
      const a1 = mag(f1), a2 = mag(f2)
      const dot = f1.x * f2.x + f1.y * f2.y
      const alpha = (a1 && a2) ? Math.acos(Math.max(-1, Math.min(1, dot / (a1 * a2)))) * 180 / Math.PI : 0
      ctx.font = 'bold 13px sans-serif'
      ctx.fillStyle = '#2563eb'; ctx.fillText(`F₁ = ${a1.toFixed(1)} N`, W - 150, 30)
      ctx.fillStyle = '#0891b2'; ctx.fillText(`F₂ = ${a2.toFixed(1)} N`, W - 150, 52)
      ctx.fillStyle = '#475569'; ctx.fillText(`góc (F₁,F₂) = ${alpha.toFixed(0)}°`, W - 150, 74)
      ctx.fillStyle = '#7c3aed'; ctx.fillText(`F = ${mag(F).toFixed(2)} N`, W - 150, 100)
      ctx.fillStyle = '#64748b'; ctx.font = '11px sans-serif'; ctx.fillText('√(F₁²+F₂²+2F₁F₂cosα)', W - 150, 118)
    } else {
      const r = axisDeg * Math.PI / 180
      const u: Vec = { x: Math.cos(r), y: Math.sin(r) }      // trục Ox'
      const v: Vec = { x: -Math.sin(r), y: Math.cos(r) }     // trục Oy'
      const fu = fA.x * u.x + fA.y * u.y                       // thành phần //trục
      const fv = fA.x * v.x + fA.y * v.y                       // thành phần ⊥trục
      const Fu: Vec = { x: u.x * fu, y: u.y * fu }
      const Fv: Vec = { x: v.x * fv, y: v.y * fv }
      // trục x' (kéo dài 2 phía)
      ctx.strokeStyle = '#f59e0b'; ctx.lineWidth = 1.5; ctx.setLineDash([6, 4])
      ctx.beginPath(); ctx.moveTo(sx({ x: -u.x * MAXF, y: -u.y * MAXF }), sy({ x: -u.x * MAXF, y: -u.y * MAXF })); ctx.lineTo(sx({ x: u.x * MAXF, y: u.y * MAXF }), sy({ x: u.x * MAXF, y: u.y * MAXF })); ctx.stroke(); ctx.setLineDash([])
      // hình chữ nhật chiếu (nét đứt)
      ctx.strokeStyle = '#94a3b8'; ctx.setLineDash([5, 4])
      ctx.beginPath(); ctx.moveTo(sx(Fu), sy(Fu)); ctx.lineTo(sx(fA), sy(fA)); ctx.lineTo(sx(Fv), sy(Fv)); ctx.stroke(); ctx.setLineDash([])
      drawArrow(ctx, fA, '#7c3aed', 'F', '', 5)
      drawArrow(ctx, Fu, '#dc2626', 'F', 'x')
      drawArrow(ctx, Fv, '#0891b2', 'F', 'y')
      ctx.font = 'bold 13px sans-serif'
      ctx.fillStyle = '#7c3aed'; ctx.fillText(`F = ${mag(fA).toFixed(1)} N`, W - 150, 30)
      ctx.fillStyle = '#475569'; ctx.fillText(`góc trục = ${axisDeg}°`, W - 150, 52)
      ctx.fillStyle = '#dc2626'; ctx.fillText(`Fₓ = F·cosθ = ${fu.toFixed(2)} N`, W - 150, 78)
      ctx.fillStyle = '#0891b2'; ctx.fillText(`F_y = F·sinθ = ${fv.toFixed(2)} N`, W - 150, 100)
    }
  }, [mode, f1, f2, fA, axisDeg])

  useEffect(() => { draw() }, [draw])

  // Kéo núm vec-tơ
  const toPhys = (e: React.PointerEvent): Vec => { const rc = cv.current!.getBoundingClientRect(); const mx = (e.clientX - rc.left) / rc.width * W; const my = (e.clientY - rc.top) / rc.height * H; return { x: (mx - O.x) / SCALE, y: (O.y - my) / SCALE } }
  const near = (v: Vec, p: Vec) => Math.hypot((sx(v) - sx(p)), (sy(v) - sy(p))) < 22
  const onDown = (e: React.PointerEvent) => {
    const p = toPhys(e); fire()
    if (mode === 'compose') { if (near(f1, p)) dragRef.current = 'f1'; else if (near(f2, p)) dragRef.current = 'f2' }
    else { if (near(fA, p)) dragRef.current = 'fA' }
    if (dragRef.current) (e.target as HTMLElement).setPointerCapture?.(e.pointerId)
  }
  const onMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return
    const p = clampMag(toPhys(e), MAXF)
    if (dragRef.current === 'f1') setF1(p); else if (dragRef.current === 'f2') setF2(p); else setFA(p)
  }
  const onUp = () => { dragRef.current = null }

  const reset = () => { fire(); setF1({ x: 6, y: 0 }); setF2({ x: 0, y: 5 }); setFA({ x: 7, y: 4 }); setAxisDeg(0) }

  return (
    <div className="rounded-2xl border border-violet-200 bg-white p-4 my-3">
      <h4 className="font-bold text-violet-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <div className="flex items-center gap-2 mb-2">
        <button onClick={() => { fire(); setMode('compose') }} className={`px-3 py-1.5 rounded-lg text-sm font-bold ${mode === 'compose' ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-600'}`}>Tổng hợp lực</button>
        <button onClick={() => { fire(); setMode('decompose') }} className={`px-3 py-1.5 rounded-lg text-sm font-bold ${mode === 'decompose' ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-600'}`}>Phân tích lực</button>
        <button onClick={reset} className="ml-auto px-3 py-1.5 rounded-lg text-sm font-bold bg-slate-100 text-slate-600 hover:bg-slate-200">↺ Đặt lại</button>
      </div>
      <canvas ref={cv} width={W} height={H}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
        className="w-full rounded-lg border border-gray-100 bg-slate-50 touch-none cursor-grab active:cursor-grabbing" />
      {mode === 'decompose' && (
        <label className="flex items-center gap-2 text-sm mt-3"><span className="w-28 text-gray-600">Góc trục Ox'</span><input type="range" min={0} max={90} step={1} value={axisDeg} onChange={(e) => { fire(); setAxisDeg(Number(e.target.value)) }} className="flex-1" /><span className="w-14 text-right font-semibold">{axisDeg}°</span></label>
      )}
      <p className="text-[11px] text-gray-400 mt-2">
        {mode === 'compose'
          ? 'Kéo đầu mỗi vec-tơ F₁, F₂ để đổi độ lớn/hướng — hợp lực F (tím) vẽ theo quy tắc hình bình hành.'
          : 'Kéo đầu vec-tơ F; xoay trục Ox\' để tách F thành Fₓ (//trục) và F_y (⊥trục) — hữu ích khi phân tích lực trên mặt phẳng nghiêng.'}
      </p>
    </div>
  )
}

export default ForceVectorSimulation
